/**
 * Änderungen am Prüfbogen – offlinefähig.
 *
 * Ablauf jeder Beurteilung:
 *
 *   1. Der Bogen im Speicher wird sofort angepasst. Der Prüfer sieht das
 *      Ergebnis ohne Wartezeit, auch ohne Empfang.
 *   2. Dieselbe Änderung geht in die Warteschlange in IndexedDB.
 *   3. Die Warteschlange wird angestoßen. Klappt es, ist der Auftrag weg;
 *      klappt es nicht, bleibt er liegen und wird später erneut versucht.
 *
 * Wichtig ist Punkt 1 vor Punkt 3, nicht umgekehrt: eine Oberfläche, die auf
 * die Antwort wartet, ist auf einem Spielplatz mit einem Balken Netz nicht
 * bedienbar.
 *
 * `beurteilt_am` setzt hier bewusst der Client als vorläufigen Wert – nur
 * damit der Punkt als beurteilt gilt. Verbindlich ist der Zeitstempel, den
 * der Server beim Eintreffen setzt; beim nächsten Laden des Bogens
 * überschreibt er den lokalen.
 */

import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { schluessel } from '../api/abfragen'
import type { BefundEingabe, Fortschritt, Pruefbogen, Stufe } from '../api/typen'
import { bogenAblegen, einreihen, fotoZwischenlegen } from '../offline/db'
import { abarbeiten } from '../offline/warteschlange'
import { verkleinern } from '../offline/bild'

/** Fortschritt aus dem Bogen neu berechnen, ohne den Server zu fragen. */
export function fortschrittRechnen(bogen: Pruefbogen): Fortschritt {
  const alle = [...bogen.platzbefunde, ...bogen.geraetebefunde]
  const beurteilt = alle.filter((b) => b.beurteilt_am !== null)
  const maengel = beurteilt.filter((b) => b.stufe === '1' || b.stufe === '2' || b.stufe === '3')

  // Erst die Platzkriterien, dann die Geräte – dieselbe Reihenfolge wie im
  // Bogen und im Bericht.
  const offenerPlatz = bogen.platzbefunde.find((b) => b.beurteilt_am === null)
  const offenesGeraet = bogen.geraetebefunde.find((b) => b.beurteilt_am === null)
  const naechster = offenerPlatz ?? offenesGeraet

  return {
    punkte_gesamt: alle.length,
    punkte_beurteilt: beurteilt.length,
    punkte_offen: alle.length - beurteilt.length,
    maengel_gesamt: maengel.length,
    stufe3_vorhanden: beurteilt.some((b) => b.stufe === '3'),
    naechster_offener_id: naechster?.id ?? null,
    naechster_offener_art: naechster ? (offenerPlatz ? 'platz' : 'geraet') : null,
    abschluss_moeglich: alle.length > 0 && beurteilt.length === alle.length,
  }
}

export function useBogenAendern(pruefungId: string) {
  const speicher = useQueryClient()

  /** Bogen im Speicher und in IndexedDB anpassen. */
  const oertlichAnpassen = useCallback(
    (wandeln: (bogen: Pruefbogen) => Pruefbogen) => {
      speicher.setQueryData<Pruefbogen>(schluessel.bogen(pruefungId), (alt) => {
        if (!alt) return alt
        const neu = wandeln(alt)
        const mitFortschritt = { ...neu, fortschritt: fortschrittRechnen(neu) }
        void bogenAblegen(mitFortschritt)
        return mitFortschritt
      })
    },
    [pruefungId, speicher],
  )

  const platzbefundBeurteilen = useCallback(
    async (befundId: string, eingabe: BefundEingabe) => {
      oertlichAnpassen((bogen) => ({
        ...bogen,
        status: bogen.status === 'entwurf' ? 'laufend' : bogen.status,
        platzbefunde: bogen.platzbefunde.map((b) =>
          b.id === befundId
            ? {
                ...b,
                stufe: eingabe.stufe,
                beurteilt_am: new Date().toISOString(),
                mangel: eingabe.mangel ?? null,
                bemerkung: eingabe.bemerkung ?? null,
                normverweis: eingabe.normverweis ?? null,
              }
            : b,
        ),
      }))

      await einreihen({
        art: 'befund',
        pruefung_id: pruefungId,
        befund_id: befundId,
        pfad: `/api/pruefungen/${pruefungId}/platzbefunde/${befundId}`,
        methode: 'PUT',
        koerper: eingabe,
        erstellt_am: Date.now(),
      })
      void abarbeiten()
    },
    [oertlichAnpassen, pruefungId],
  )

  const geraetebefundBeurteilen = useCallback(
    async (befundId: string, eingabe: BefundEingabe) => {
      const sperrt = eingabe.stufe === '3'
      const istMangel = ['1', '2', '3'].includes(eingabe.stufe)

      oertlichAnpassen((bogen) => ({
        ...bogen,
        status: bogen.status === 'entwurf' ? 'laufend' : bogen.status,
        geraetebefunde: bogen.geraetebefunde.map((b) =>
          b.id === befundId
            ? {
                ...b,
                stufe: eingabe.stufe,
                beurteilt_am: new Date().toISOString(),
                mangel: eingabe.mangel ?? null,
                bemerkung: eingabe.bemerkung ?? null,
                normverweis: eingabe.normverweis ?? null,
                frist_am: eingabe.frist_am ?? null,
                // Die Sperre ist Folge der Stufe, keine eigene Entscheidung.
                gesperrt: sperrt,
                mangelstatus: istMangel ? 'offen' : null,
              }
            : b,
        ),
      }))

      await einreihen({
        art: 'befund',
        pruefung_id: pruefungId,
        befund_id: befundId,
        pfad: `/api/pruefungen/${pruefungId}/geraetebefunde/${befundId}`,
        methode: 'PUT',
        koerper: eingabe,
        erstellt_am: Date.now(),
      })
      void abarbeiten()
    },
    [oertlichAnpassen, pruefungId],
  )

  const zuruecksetzen = useCallback(
    async (befundId: string, istGeraet: boolean) => {
      oertlichAnpassen((bogen) => ({
        ...bogen,
        platzbefunde: istGeraet
          ? bogen.platzbefunde
          : bogen.platzbefunde.map((b) =>
              b.id === befundId
                ? {
                    ...b,
                    stufe: null,
                    beurteilt_am: null,
                    mangel: null,
                    bemerkung: null,
                    normverweis: null,
                  }
                : b,
            ),
        geraetebefunde: istGeraet
          ? bogen.geraetebefunde.map((b) =>
              b.id === befundId
                ? {
                    ...b,
                    stufe: null,
                    beurteilt_am: null,
                    mangel: null,
                    bemerkung: null,
                    normverweis: null,
                    // Die Sperre muss mit zurück, sonst bleibt ein Gerät
                    // grundlos gesperrt.
                    gesperrt: false,
                    mangelstatus: null,
                    frist_am: null,
                  }
                : b,
            )
          : bogen.geraetebefunde,
      }))

      const teil = istGeraet ? 'geraetebefunde' : 'platzbefunde'
      await einreihen({
        art: 'zuruecksetzen',
        pruefung_id: pruefungId,
        befund_id: befundId,
        pfad: `/api/pruefungen/${pruefungId}/${teil}/${befundId}/zuruecksetzen`,
        methode: 'POST',
        koerper: {},
        erstellt_am: Date.now(),
      })
      void abarbeiten()
    },
    [oertlichAnpassen, pruefungId],
  )

  /**
   * Foto aufnehmen.
   *
   * Wird sofort verkleinert, in IndexedDB gelegt und eingereiht. Die
   * Verkleinerung passiert vor dem Ablegen, weil 30 Aufnahmen à 8 MB das
   * Speicherkontingent des Browsers überschreiten würden.
   */
  const fotoAufnehmen = useCallback(
    async (datei: File, befundId: string | undefined, art: 'uebersicht' | 'detail') => {
      const verkleinert = await verkleinern(datei)
      const fotoSchluessel = crypto.randomUUID()

      await fotoZwischenlegen({
        schluessel: fotoSchluessel,
        pruefung_id: pruefungId,
        befund_id: befundId,
        art,
        blob: verkleinert.blob,
        breite: verkleinert.breite,
        hoehe: verkleinert.hoehe,
      })

      await einreihen({
        art: 'foto',
        pruefung_id: pruefungId,
        befund_id: befundId,
        pfad: `/api/pruefungen/${pruefungId}/fotos`,
        methode: 'POST',
        foto_schluessel: fotoSchluessel,
        erstellt_am: Date.now(),
      })

      // Zähler im Bogen sofort hochsetzen, damit der Prüfer die Aufnahme
      // bestätigt sieht.
      if (befundId) {
        oertlichAnpassen((bogen) => ({
          ...bogen,
          geraetebefunde: bogen.geraetebefunde.map((b) =>
            b.id === befundId ? { ...b, anzahl_fotos: b.anzahl_fotos + 1 } : b,
          ),
          platzbefunde: bogen.platzbefunde.map((b) =>
            b.id === befundId ? { ...b, anzahl_fotos: b.anzahl_fotos + 1 } : b,
          ),
        }))
      }

      void abarbeiten()
      return verkleinert
    },
    [oertlichAnpassen, pruefungId],
  )

  /** Verwendung eines Textbausteins zählen – nach Häufigkeit sortiert die Liste. */
  const bausteinVerwendet = useCallback(
    async (bausteinId: string) => {
      await einreihen({
        art: 'baustein',
        pruefung_id: pruefungId,
        pfad: `/api/kataloge/textbausteine/${bausteinId}/verwendet`,
        methode: 'POST',
        erstellt_am: Date.now(),
      })
      void abarbeiten()
    },
    [pruefungId],
  )

  return {
    platzbefundBeurteilen,
    geraetebefundBeurteilen,
    zuruecksetzen,
    fotoAufnehmen,
    bausteinVerwendet,
  }
}

export type { Stufe }
